import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminDomain } from '../utils/domainUtils';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AddCircle as AddCircleIcon,
  BarChart as BarChartIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { AGENTS } from '../types/auth';

const Navbar = () => {
  const { currentUser, userProfile, userPermissions, selectedAgent, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Detect current domain
  const isAdmin = isAdminDomain();
  const siteName = isAdmin ? 'Tabet Dairies - Admin' : 'Tabet Dairies';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Get menu items based on user permissions
  const getMenuItems = () => {
    const baseItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
      { text: 'Sales', icon: <AddCircleIcon />, path: '/sales' },
      { text: 'Profile', icon: <PersonIcon />, path: '/profile' }
    ];

    // Add Reports & Ledger only if user can manage ledger
    if (userPermissions?.canManageLedger) {
      baseItems.splice(3, 0, { text: 'Reports & Ledger', icon: <BarChartIcon />, path: '/reports' });
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        {siteName}
      </Typography>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem 
            key={item.text} 
            component={Link} 
            to={item.path}
            sx={{ 
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{ 
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar position="static">
      <Toolbar>
        {currentUser && isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            color: 'inherit',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {siteName}
        </Typography>

        {currentUser ? (
          isMobile ? (
            <Drawer
              variant="temporary"
              anchor="left"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true // Better open performance on mobile
              }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 }
              }}
            >
              {drawer}
            </Drawer>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* User Role and Agent Info */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {userProfile?.role === 'super_admin' ? (
                  <Chip
                    icon={<SecurityIcon />}
                    label="Super Admin"
                    color="error"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<AdminIcon />}
                    label={selectedAgent ? AGENTS[selectedAgent].name : 'Admin'}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Box>
          )
        ) : (
          <Button
            color="inherit"
            component={Link}
            to="/login"
          >
            Login
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 